import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api.js';
import { beginSession } from '@/lib/session.js';
import {
  loadMethods, loginView, surfaceOf, gisOptions, readReturnFragment, postGoogleCredential, loadGisScript,
  signInWords, passwordWords, RETURN_KEY, PASSWORD_PATH, newNonce, markRedirectPending, takeRedirectPending, acceptReturn, FULL_PAGE_NOTE,
} from '@/lib/sign-in.js';

// Broker sign-in (personal-logins L5, PLAN 8, R2). Google first, the shared password only while
// GET /auth/methods allows it; every decision is lib/sign-in.js's (tests/login-methods-skew,
// tests/gis-redirect-fallback). A methods door that is missing or wrong keeps today's password form.
//
// Always show the page on /login, no "already authenticated" short-circuit: an earlier version
// redirected on isAuthenticated() and deadlocked a broker holding a stale token (2026-05-19).

const DEFAULT_RETURN = '/command-center';
const safeReturn = (p) => (typeof p === 'string' && p.startsWith('/') && !p.startsWith('//') ? p : DEFAULT_RETURN);
const store = {
  get: () => { try { return sessionStorage.getItem(RETURN_KEY); } catch { return null; } },
  set: (v) => { try { sessionStorage.setItem(RETURN_KEY, v); } catch { /* private mode */ } },
  drop: () => { try { sessionStorage.removeItem(RETURN_KEY); } catch { /* private mode */ } },
};
const session = () => { try { return sessionStorage; } catch { return null; } };
const pending = {
  mark: (nonce) => { const ss = session(); return ss ? markRedirectPending(ss, { nonce }) : false; },
  take: () => { const ss = session(); return ss ? takeRedirectPending(ss) : null; },
};

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [methods, setMethods] = useState(null);
  const [popupFailed, setPopupFailed] = useState(false);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturn(params.get('return') || store.get());
  const surface = surfaceOf(window);
  const view = loginView(methods, window.location.origin);

  async function finish(token) {
    await beginSession(token);
    store.drop();
    navigate(returnTo, { replace: true });
  }

  // The GIS redirect callback lands here with #t=<token>&s=<nonce> or #e=<code>; the fragment leaves the
  // URL first, and a token counts only with the nonce of the sign-in this tab started (login CSRF).
  useEffect(() => {
    const back = readReturnFragment(window.location.hash);
    if (!window.location.hash) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    const started = pending.take();
    const r = acceptReturn(back, started);
    if (r && r.token) finish(r.token);
    else if (r) setError(signInWords(r.code));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let live = true;
    loadMethods().then((m) => { if (live) setMethods(m); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!view.google || !buttonRef.current) return undefined;
    let live = true;
    async function onCredential(resp) {
      setError(null);
      setLoading(true);
      const r = await postGoogleCredential({ credential: resp && resp.credential, surface });
      setLoading(false);
      if (r.ok) await finish(r.token);
      else setError(signInWords(r.code));
    }
    loadGisScript().then(() => {
      if (!live || !buttonRef.current) return;
      const gid = window.google && window.google.accounts && window.google.accounts.id;
      if (!gid) throw new Error('gis_load_failed');
      const nonce = newNonce();
      const opts = gisOptions({ clientId: view.clientId, origin: window.location.origin, surface, popupFailed, callback: onCredential, nonce });
      const redirect = opts.ux_mode === 'redirect';
      if (redirect) store.set(returnTo);
      gid.initialize(opts);
      buttonRef.current.replaceChildren();
      gid.renderButton(buttonRef.current, {
        type: 'standard', theme: 'outline', size: 'large', text: 'signin_with', shape: 'rectangular', width: 300,
        ...(redirect ? { click_listener: () => pending.mark(nonce) } : {}),
      });
    }).catch(() => { if (live) setError(signInWords('gis_load_failed')); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.google, view.clientId, popupFailed, surface]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post(PASSWORD_PATH, { username, password }, {
        expected: [{ status: 401, code: 'Invalid credentials' }, { status: 410, code: 'shared_login_off' }, { status: 503, code: 'identity_unavailable' }],
      });
      if (res?.success && res.token) await finish(res.token);
      else setError(passwordWords(0));
    } catch (err) {
      setError(passwordWords(err?.status, err?.body?.code));
      if (err?.status === 410) loadMethods().then(setMethods);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="w-full max-w-sm rounded-card border border-border bg-surface p-6 shadow-2xl">
        <h1 className="text-base font-semibold uppercase tracking-[0.18em] text-text-muted">
          TRACON
        </h1>
        <p className="mt-1 text-sm text-text-dim">Broker sign-in</p>

        {view.google && (
          <div className="mt-6">
            <div ref={buttonRef} data-google-button className="flex min-h-[44px] justify-center" />
            {surface === 'browser' && popupFailed && <p className="mt-2 text-center text-xs text-text-muted">{FULL_PAGE_NOTE}</p>}
            {surface === 'browser' && !popupFailed && (
              <button
                type="button"
                onClick={() => { setError(null); setPopupFailed(true); }}
                className="mt-2 block w-full text-center text-xs text-text-dim underline-offset-2 hover:text-text hover:underline"
              >
                Google window did not open? Use the full-page sign-in
              </button>
            )}
          </div>
        )}

        {view.google && view.password && (
          <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-wide text-text-dim">
            <span className="h-px flex-1 bg-border" />
            or the shared login
            <span className="h-px flex-1 bg-border" />
          </div>
        )}

        {!view.google && !view.password && (
          <p className="mt-6 text-sm text-text-muted">{signInWords(view.why)}</p>
        )}

        {view.password && (
          <form onSubmit={handleSubmit}>
            <label className="mt-6 block text-xs font-medium uppercase tracking-wide text-text-muted">
              Username
              <input
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1.5 block w-full rounded-card border border-border bg-bg px-3 py-2.5 text-sm text-text outline-none focus:border-accent min-h-[44px]"
              />
            </label>

            <label className="mt-4 block text-xs font-medium uppercase tracking-wide text-text-muted">
              Password
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 block w-full rounded-card border border-border bg-bg px-3 py-2.5 text-sm text-text outline-none focus:border-accent min-h-[44px]"
              />
            </label>

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="mt-6 flex w-full items-center justify-center rounded-card bg-accent px-4 py-2.5 text-sm font-medium text-white transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50 min-h-[44px]"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        )}

        {error && (
          <p role="alert" className="mt-4 rounded-card border border-accent/40 bg-accent-soft px-3 py-2 text-sm text-accent">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
